import { h, ComponentChildren } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { parseToDateAndFormatIfExists } from "../utils";
import { cn } from "../helpers/cn";

function checkFilter(value: string, search: string, type: "text" | "date") {
    if (type === "text") {
        return value.toLowerCase().indexOf(search.toLowerCase()) >= 0;
    } else if (type === "date") {
        return parseToDateAndFormatIfExists(value).toLocaleString().indexOf(search) >= 0;
    }

    return false;
}

function deepAccess(obj: any, props: string) {
    const propArray = props.split(".");
    let prop = propArray.shift();

    while (prop) {
        obj = obj[prop];
        prop = propArray.shift();
    }

    return obj;
}

export function CompactTableItemSelector<T extends { [key: string]: any }>(props: {
    items: T[];
    onSelect: (item: T | null) => void;
    selected?: T | null;
    properties: {
        name: string;
        type: "text" | "date";
        title?: string;
        search: boolean;
        format?: (value: string) => ComponentChildren;
        valueProvider?: (item: T) => string;
        filter?: string[];
    }[];
    class?: string;
}) {
    const [filteredItems, setFilteredItems] = useState(props.items);
    const [search, setSearch] = useState("");
    const [selectedItem, setSelectedItem] = useState(props.selected ?? null);
    const [filters, setFilters] = useState({} as { [key: string]: string });

    useMemo(() => setSelectedItem(props.selected ?? null), [props.selected]);

    useEffect(() => {
        if (props.items) {
            const filtered = props.items.filter(item => {
                let result = true;

                if (search) {
                    result = props.properties.some(
                        v =>
                            v.search &&
                            checkFilter(
                                v.valueProvider ? v.valueProvider(item) : deepAccess(item, v.name),
                                search,
                                v.type,
                            ),
                    );
                }

                props.properties
                    .filter(v => v.filter)
                    .forEach(v => {
                        const format = v.format ?? ((x: string) => x);
                        result &&=
                            !filters[v.name] ||
                            format(v.valueProvider ? v.valueProvider(item) : deepAccess(item, v.name)) ===
                                filters[v.name];
                    });

                return result;
            });

            setFilteredItems(filtered);
        } else {
            setFilteredItems(props.items);
        }
    }, [props.items, search, selectedItem, filters]);

    return (
        <div>
            <div class="search flex gap-2">
                {props.properties
                    .filter(v => v.filter)
                    .map(v => (
                        <label class="flex items-center gap-1">
                            {v.title}:
                            <select onChange={e => setFilters(prev => ({ ...prev, [v.name]: e.currentTarget.value }))}>
                                <option value=""></option>
                                {v.filter!.map(v => (
                                    <option value={v}>{v}</option>
                                ))}
                            </select>
                        </label>
                    ))}
                <input
                    class="search-text"
                    placeholder="Wyszukaj..."
                    value={search}
                    onInput={e => {
                        setSearch(e.currentTarget.value);
                        setSelectedItem(null);
                        props.onSelect(null);
                    }}
                />
            </div>
            <div
                class={cn("max-h-52", props.class)}
                style="margin-top: 10px; overflow: auto; position: relative; border-bottom: 1px solid #ffffff24"
            >
                <table class="list selectable" style="border-top: none; border-bottom: none;">
                    <thead style="position: sticky; top: 0; background-color: #2f2f2f;">
                        <tr>
                            {props.properties
                                .filter(v => v.title)
                                .map(v => (
                                    <th style="padding: 5px 5px; border-top: 1px solid #ffffff24; border-bottom: 1px solid #ffffff10;">
                                        {v.title}
                                    </th>
                                ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filteredItems.map(item => (
                            <tr
                                class={"item" + (item === selectedItem ? " selected" : "")}
                                onClick={() => {
                                    setSelectedItem(selectedItem === item ? null : item);
                                    props.onSelect(selectedItem === item ? null : item);
                                }}
                            >
                                {props.properties
                                    .filter(v => v.title)
                                    .map(v => (
                                        <td>
                                            {v.type === "text"
                                                ? v.format
                                                    ? v.format(
                                                          v.valueProvider
                                                              ? v.valueProvider(item)
                                                              : deepAccess(item, v.name),
                                                      )
                                                    : v.valueProvider
                                                      ? v.valueProvider(item)
                                                      : deepAccess(item, v.name)
                                                : parseToDateAndFormatIfExists(
                                                      v.valueProvider
                                                          ? v.valueProvider(item)
                                                          : deepAccess(item, v.name),
                                                  )}
                                        </td>
                                    ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
